import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { useRevalidateOnFocus } from "@/hooks/useRevalidateOnFocus";
import { realtimeTopic } from "@/lib/realtime/channel";
import { useParams, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { AlertCircle, Bell } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useNotificationsStore } from "@/stores/pm/hooks/usePMStore";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { ChatLauncherSpacer } from "@/components/layout/ChatLauncherSpacer";
import { Button } from "@/components/ui/button";
import { PMEmptyState, PMLoadingState } from "@/domains/pm/components/shared";
import { ActorAvatar } from "@/domains/pm/components/shared/ActorAvatar";
import { renderActivityLine } from "@/domains/pm/utils/activityLine";
import { ACTIVITY_ICON, categoriesToActionTypes } from "@/domains/pm/utils/activityMeta";
import {
  NotificationsFilterControl,
  EMPTY_NOTIFICATIONS_FILTER,
  type NotificationsFilterValue,
} from "@/pages/pm/components/NotificationsFilterControl";
import type { PMIssueActivity, UserProfile } from "@/domains/pm/types";
import { PageTitle } from "@/components/ui/page-title";
import { PageSubtitle } from "@/components/ui/page-subtitle";

/** Full date + time for a feed row, e.g. 17.06.2026 14:32. */
function formatDateTime(value: string, language: "en" | "et"): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(language === "et" ? "et-EE" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Day header key for grouping, locale-formatted. */
function dayKey(value: string, language: "en" | "et"): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(language === "et" ? "et-EE" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * WHA-3733 — "Notifications": a building-scoped, project-filterable feed of how
 * every issue the user can access has been created and changed. Reads the shared
 * pm_issue_activity log (RLS scopes it to the user's projects). Opening the page
 * marks notifications seen, which clears the nav red dot.
 */
const PMNotifications = observer(function PMNotifications() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const notificationsStore = useNotificationsStore();
  const { userProfile } = useAuth();
  const orgStore = useOrganizationStore();
  const { currentOrganization } = useOrganizationContext();

  // The route param is a string; every read below wants the number, and a
  // non-numeric param must disable them rather than query `eq.NaN`.
  const buildingIdNum = Number(id);
  const hasBuildingId = Number.isFinite(buildingIdNum);

  // Global building scope — the feed shows only the scoped project's issue
  // activity. The per-page project dropdown was removed in favour of the
  // building-level project scope selected from the sidebar.
  const { selectedProjectId } = useBuildingScope(id ?? "");
  const [filter, setFilter] = useState<NotificationsFilterValue>(EMPTY_NOTIFICATIONS_FILTER);

  // Feed data, pagination, and loading/error live in the MST store (WHA-3737),
  // mirroring how IssuesStore owns the issue list; the component only holds UI
  // filter selection and reads store observables via observer().
  const rows = notificationsStore.feed;

  // Org users power the "Person" filter dimension.
  useEffect(() => {
    if (currentOrganization?.id) {
      orgStore.fetchOrganizationUsers(currentOrganization.id);
    }
  }, [currentOrganization?.id, orgStore]);

  const orgUsers: UserProfile[] = orgStore.users.map((u) => ({
    id: u.id,
    first_name: u.first_name,
    last_name: u.last_name,
    email: u.email,
    avatar_url: u.avatar_url,
    color: u.color,
  }));

  // Build the repo query options from the current filter selection. `filter` is
  // a fresh object on every setFilter, so this recomputes (and the fetch effect
  // below re-runs) exactly when the project, filter, or user changes.
  const feedOpts = useMemo(
    () => ({
      buildingProjectId: selectedProjectId ?? undefined,
      actionTypes: categoriesToActionTypes(filter.categories),
      actorIds: filter.actorIds.length ? filter.actorIds : undefined,
      timeRange: { timeRange: filter.timeRange, customRange: filter.customRange },
      unreadOnly: filter.unreadOnly,
      currentUserId: userProfile?.id,
    }),
    [selectedProjectId, filter, userProfile?.id]
  );

  // Initial + filter-change fetch — delegated to the store. This one is
  // allowed to show the loading state; the background reads below are not.
  useEffect(() => {
    if (!hasBuildingId) return;
    notificationsStore.loadFeed(buildingIdNum, feedOpts);
  }, [hasBuildingId, buildingIdNum, feedOpts, notificationsStore]);

  // The feed was read once per filter change and never again, so an open
  // activity feed simply never gained a row. `refreshFeed`, not `loadFeed`: a
  // teammate's activity must not blank the list behind a spinner or throw away
  // the pages the user pulled in with "show more". It re-reads the query the
  // effect above remembered, so it needs no deps of its own.
  const reloadFeed = useCallback(() => {
    notificationsStore.refreshFeed();
  }, [notificationsStore]);

  useRealtimeInvalidate({
    enabled: hasBuildingId,
    topic: realtimeTopic("pm-activity-feed", buildingIdNum),
    bindings: [
      { event: "*", table: "pm_issue_activity", filter: `building_id=eq.${buildingIdNum}` },
    ],
    onChange: reloadFeed,
    // Trigger-written, several rows per issue save — see subscribeToActivity.
    windowMs: 750,
  });

  useRevalidateOnFocus(reloadFeed, { enabled: hasBuildingId });

  // Drop the cached feed on unmount so a different building doesn't flash stale
  // rows on the next visit.
  useEffect(() => () => notificationsStore.clearFeed(), [notificationsStore]);

  // Opening the page = "seen". Clears the nav red dot and stamps last_seen.
  useEffect(() => {
    notificationsStore.markSeen().then(() => {
      window.dispatchEvent(new CustomEvent("franklin:pm-notifications-seen"));
    });
  }, [notificationsStore]);

  const loadMore = useCallback(() => notificationsStore.loadMoreFeed(), [notificationsStore]);

  /**
   * Pull the next page in as the reader reaches the end of this one, so the
   * feed simply keeps scrolling. The button below stays: it is the affordance
   * for a keyboard user, and the fallback wherever IntersectionObserver is
   * unavailable or the sentinel never scrolls into view.
   *
   * `loadMoreFeed` ignores a call while one is already in flight, so the
   * observer firing repeatedly during a fast scroll costs nothing.
   */
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const hasMore = notificationsStore.hasMoreFeed;
  // Re-observing after each append matters: if the page just added is short
  // enough that the sentinel never leaves the viewport, the observer has no
  // new crossing to report and the feed would stall one page short.
  const loadedCount = rows.length;
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      // Start the read a screenful early so the rows are there by the time
      // the reader arrives.
      { rootMargin: "400px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadedCount, loadMore]);

  // Group the flat reverse-chronological feed by calendar day for readability.
  const grouped = useMemo(() => {
    const out: { day: string; items: PMIssueActivity[] }[] = [];
    for (const row of rows) {
      const day = dayKey(row.created_at, language);
      const last = out[out.length - 1];
      if (last && last.day === day) last.items.push(row);
      else out.push({ day, items: [row] });
    }
    return out;
  }, [rows, language]);

  const openIssue = (issueId: number) => {
    navigate(`/buildings/${id}/issues?selected=${issueId}`);
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-7 pt-4">
        <div className="mb-3">
          <PageTitle info="notifications">{t.pm.notifications.title}</PageTitle>
          <PageSubtitle className="mt-1">{t.pm.notifications.subtitle}</PageSubtitle>
        </div>

        {/* Filters sit at the right end of the toolbar row, the same side the
            Issues and Documents toolbars put theirs on. */}
        <div className="mb-3 flex flex-wrap items-center justify-end gap-4">
          <NotificationsFilterControl users={orgUsers} value={filter} onChange={setFilter} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-7 py-4">
        {notificationsStore.feedError && (
          <div
            role="alert"
            className="mb-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{notificationsStore.feedError}</span>
          </div>
        )}

        {notificationsStore.isLoadingFeed ? (
          <PMLoadingState />
        ) : rows.length === 0 ? (
          !notificationsStore.feedError && (
            <PMEmptyState
              icon={Bell}
              title={t.pm.notifications.empty.title}
              description={t.pm.notifications.empty.subtitle}
            />
          )
        ) : (
          <div className="max-w-3xl space-y-6">
            {grouped.map((group) => (
              <div key={group.day}>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {group.day}
                </p>
                <ul className="space-y-1">
                  {group.items.map((a) => {
                    const TypeIcon = ACTIVITY_ICON[a.action_type];
                    return (
                      <li key={a.id}>
                        <button
                          type="button"
                          onClick={() => openIssue(a.issue_id)}
                          className="flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-muted/60"
                        >
                          <TypeIcon className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                          <ActorAvatar actor={a.actor} size={24} className="mt-0.5" />
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="text-sm leading-snug text-foreground">
                              {renderActivityLine(a, t, language)}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {a.issue_number != null && (
                                <span className="font-medium">#{a.issue_number} </span>
                              )}
                              {a.issue_title}
                              {a.issue_title ? " · " : ""}
                              {formatDateTime(a.created_at, language)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {notificationsStore.hasMoreFeed && (
              <div ref={sentinelRef} className="flex justify-start pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={notificationsStore.isLoadingMore}
                >
                  {notificationsStore.isLoadingMore
                    ? t.pm.issues.detail.processing
                    : t.common.showMore}
                </Button>
              </div>
            )}
          </div>
        )}
        <ChatLauncherSpacer />
      </div>
    </div>
  );
});

export default PMNotifications;
